import { baseApi } from "@/shared/api";
import { type EducationDto, type EducationMutationResponse, type EducationRecord } from "./types";

function buildEducationFormData(body: Partial<EducationDto>): FormData {
  const form = new FormData();

  if (body.degree !== undefined) form.append("degree", body.degree);
  if (body.university_name !== undefined) form.append("university_name", body.university_name);
  if (body.specialty !== undefined) form.append("specialty", body.specialty);
  if (body.admission_year !== undefined) form.append("admission_year", body.admission_year);
  if (body.graduation_year !== undefined) form.append("graduation_year", body.graduation_year);
  if (body.diploma_number !== undefined) form.append("diploma_number", body.diploma_number);
  if (body.is_incomplete !== undefined) form.append("is_incomplete", String(body.is_incomplete));
  if (body.left_from_course !== undefined) form.append("left_from_course", body.left_from_course);
  if (body.diploma_file) form.append("diploma_file", body.diploma_file);
  if (body.diploma_transcript_file) form.append("diploma_transcript_file", body.diploma_transcript_file);

  return form;
}

export const educationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getEducation: builder.query<EducationRecord[], string>({
      query: (token) => `/api/hr/apply/${token}/education/`,
      providesTags: ["Completeness"],
    }),

    createEducation: builder.mutation<EducationMutationResponse, { token: string; body: Partial<EducationDto> }>({
      query: ({ token, body }) => ({
        url: `/api/hr/apply/${token}/education/`,
        method: "POST",
        body: buildEducationFormData(body),
      }),
      invalidatesTags: ["Completeness"],
    }),

    updateEducation: builder.mutation<
      EducationMutationResponse,
      { token: string; id: number; body: Partial<EducationDto> }
    >({
      query: ({ token, id, body }) => ({
        url: `/api/hr/apply/${token}/education/${id}/`,
        method: "PATCH",
        body: buildEducationFormData(body),
      }),
      invalidatesTags: ["Completeness"],
    }),

    deleteEducation: builder.mutation<void, { token: string; id: number }>({
      query: ({ token, id }) => ({
        url: `/api/hr/apply/${token}/education/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Completeness"],
    }),

    setEducationFlag: builder.mutation<void, { token: string; has_no_education: boolean }>({
      query: ({ token, has_no_education }) => ({
        url: `/api/hr/apply/${token}/education-flag/`,
        method: "PATCH",
        body: { has_no_education },
      }),
      invalidatesTags: ["Completeness"],
    }),
  }),
});

export const {
  useGetEducationQuery,
  useCreateEducationMutation,
  useUpdateEducationMutation,
  useDeleteEducationMutation,
  useSetEducationFlagMutation,
} = educationApi;
